import { BandChart, SkeletonLoader } from '@toggle/design-system';
import { CalculatorResponse } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getHorizonToIndexMap } from '~/components/price-movement/components/price-movement-chart/utils/price-movement-chart-utils';
import { useThemePerformance } from '~/react-query-hooks/themes/useThemeOverview';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';
import { useChartTheme } from '~/views/turbo-chart/hooks/use-chart-theme/useChartTheme';

import { PriceReturnPill } from './price-return-pill/PriceReturnPill';
import {
  SimpleHorizon,
  SimpleHorizonSelector,
} from './simple-horizon-selector/SimpleHorizonSelector';
import * as S from './ThemePerformanceSection.styles';

interface RevampThemePerformanceSectionProps {
  entityTags: string[];
  isFetching?: boolean;
}

export const RevampThemePerformanceSection = ({
  entityTags,
  isFetching,
}: RevampThemePerformanceSectionProps) => {
  const { t } = useTranslation('widget');
  const { colors } = useChartTheme(['soft']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const [selectedHorizon, setSelectedHorizon] = useState<SimpleHorizon>('12M');
  const horizonToIndexMap = getHorizonToIndexMap(1);

  const {
    data: themePerformanceData,
    isFetching: isThemePerformanceFetching,
    error: themePerformanceError,
  } = useThemePerformance(
    entityTags,
    horizonToIndexMap.get(selectedHorizon.toLowerCase())
  );

  const total = entityTags.length > 10 ? 10 : entityTags.length;

  const formatData = (data?: CalculatorResponse) =>
    data?.aggregate.map((d, idx) => ({
      index: idx,
      value: d.value,
      date: new Date(d.index),
    })) ?? [];

  const handleHorizonChange = (horizon: SimpleHorizon) => {
    setSelectedHorizon(horizon);
  };

  const tsData = formatData(themePerformanceData);
  const lastValue = tsData.at(-1)?.value;

  if (themePerformanceError) {
    throw themePerformanceError;
  }

  return (
    <S.Root>
      <S.ChartTitleWrapper>
        <S.ChartTitleText>
          {t('widget:themeOverview.themePerformanceTitle')}
        </S.ChartTitleText>
        <SimpleHorizonSelector
          selectedHorizon={selectedHorizon}
          horizons={['1W', '1M', '3M', '6M', '12M']}
          handleHorizonChange={handleHorizonChange}
        />
      </S.ChartTitleWrapper>
      <S.DescriptionWrapper>
        <S.DescriptionText>
          {`${t('themeOverview.equalWeightedBasket')} ${t(
            'themeOverview.topCompanies',
            { total }
          )}`}
        </S.DescriptionText>
        <PriceReturnPill horizon={selectedHorizon} tsValue={lastValue} />
      </S.DescriptionWrapper>
      {isThemePerformanceFetching || !!isFetching ? (
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '248px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      ) : (
        <S.ChartAndHorizonsWrapper>
          <S.BandChartWrapper $color={colors.default.value}>
            <BandChart
              tsData={tsData}
              showLine
              domainLimited={false}
              includeXScalePadding={false}
              isBordered
              fillColors={{
                positive: colors.threshold.positive.value,
                negative: colors.threshold.negative.value,
              }}
              isPercent
              locale={languageSetting}
            />
          </S.BandChartWrapper>
        </S.ChartAndHorizonsWrapper>
      )}
    </S.Root>
  );
};
